import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import api from '@/api/client'

/** Returns true if the JWT is already expired (exp < now). */
function isTokenExpired(token: string): boolean {
  try {
    // JWT uses base64url encoding — convert to base64 before atob()
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64))
    return typeof payload.exp === 'number' && payload.exp < Date.now() / 1000
  } catch {
    return false  // On parse failure assume valid — let 401 handling take over
  }
}

/**
 * On mount and on window focus:
 *  1. If no token → nothing (ProtectedRoute handles it)
 *  2. If token expired → try silent refresh via /v1/auth/token/refresh
 *  3. If refresh fails → logout + redirect to /login
 */
export function useSessionGuard() {
  const navigate        = useNavigate()
  const logout          = useAuthStore(s => s.logout)
  const accessToken     = useAuthStore(s => s.accessToken)
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  const validate = useCallback(async () => {
    // Only run when Zustand has hydrated and considers the user authenticated
    if (!isAuthenticated) return

    // Durante l'impersonificazione il token rilevante è quello in sessionStorage
    // (gestito a parte, non rinnovabile col refresh token dell'admin). Non
    // validare/refreshare il token dell'admin: lo farebbe scadere e provocherebbe
    // un redirect/logout immediato ("la pagina compare e scompare").
    if (sessionStorage.getItem('impersonationToken')) return

    const token = accessToken ?? localStorage.getItem('accessToken')
    if (!token) return                         // no token — ProtectedRoute will redirect
    if (!isTokenExpired(token)) return         // still valid — nothing to do

    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      logout()
      navigate('/login', { replace: true })
      return
    }

    try {
      await api.post('/v1/auth/token/refresh', { refreshToken })
      // interceptor already stored the new token via setAuth
    } catch {
      logout()
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, accessToken, logout, navigate])

  // Run on mount — small delay to ensure Zustand has hydrated
  useEffect(() => {
    const t = setTimeout(validate, 200)
    return () => clearTimeout(t)
  }, [validate])

  // Run on window focus (catches expiry while tab was in background)
  useEffect(() => {
    window.addEventListener('focus', validate)
    return () => window.removeEventListener('focus', validate)
  }, [validate])
}
